/**
 * list_schedules Tool - Lists recurring test schedules for a project
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';
import { FREQUENCY_LABELS, WEEKDAY_SHORT_LABELS, type ScheduleWeekday } from '@runhuman/shared';

export interface ListSchedulesArgs {
  projectId: string;
  limit?: number;
}

export const listSchedulesToolDefinition: Tool = {
  name: 'list_schedules',
  description: `Lists recurring test schedules for a project.

Schedules automatically run a template on a repeating cadence (daily, weekly, biweekly, monthly, or one-time).

Each schedule shows its frequency, next execution time, and status (active/paused).
Use create_schedule to set up new recurring tests.`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID to list schedules for (required).',
      },
      limit: {
        type: 'number',
        description: 'Maximum number of schedules to return. Default: 50.',
      },
    },
    required: ['projectId'],
  },
};

function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function formatWeekdays(weekdays: ScheduleWeekday[]): string {
  return weekdays.map(d => WEEKDAY_SHORT_LABELS[d]).join(', ');
}

export async function handleListSchedules(
  args: ListSchedulesArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const data = await jobClient.listSchedules(args.projectId, args.limit);

    if (data.items.length === 0) {
      return {
        content: [{
          type: 'text',
          text: `No schedules found for this project.

Use create_schedule to set up recurring tests from a template.`,
        }],
      };
    }

    const scheduleList = data.items.map(s => {
      const freq = FREQUENCY_LABELS[s.frequency];
      const time = `${formatTime(s.scheduledHour, s.scheduledMinute)} ${s.timezone}`;
      let details = `- "${s.name}" [${s.status}]
  Template: ${s.templateName}
  Frequency: ${freq}`;
      if (s.weekdays.length > 0) details += ` on ${formatWeekdays(s.weekdays)}`;
      if (s.dayOfMonth) details += ` on day ${s.dayOfMonth === 0 ? 'last' : s.dayOfMonth}`;
      details += `\n  Time: ${time}`;
      details += `\n  Next run: ${s.nextExecutionAt}`;
      details += `\n  ID: ${s.id}`;
      return details;
    }).join('\n\n');

    return {
      content: [{
        type: 'text',
        text: `Found ${data.items.length} schedule(s):

${scheduleList}

To manage: update_schedule(scheduleId, ...) or delete_schedule(scheduleId)`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to list schedules\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
